import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { getHardCodedIds, isSet } from "../../../Utils/CommonFunctions";
import Icons from "../../Icons/Icons";
import Buttons from "../../UI/Buttons";
import PagesGroupEditForm from "./PagesGroupEditForm";
import ScmFormInputWrapper from "./ScmFormInputWrapper";
import { extractFieldValue, extractOptions, extractStylesAsObjects } from "../MarkupFunctions";
import {
  addRow,
  addRowInEdit,
  beginRowEdit,
  cloneRowSnapshot,
  collectColumnNamesFromChildren,
  collectFieldNodesFromChildren,
  copyRow,
  deleteRow,
  deleteSelectedRows,
  endRowEdit,
  formatCellDisplayValue,
  readRowsFromFormPayload,
  saveAllOpenRows,
  saveOneRowRevertOthers,
  setAllRowsSelected,
  setRowSelected,
  updateRowField,
} from "./utils/pagesSubformRows";

/** Map pages scm:inputtext type → FormBuilder SwitchItems element for GroupEditForm. */
const mapPagesTypeToGroupEditElement = (rawType) => {
  const type = String(rawType || "TextInput").trim();
  if (type === "Picklist" || type === "PickList") return "PickList";
  if (type === "Text" || type === "text") return "TextInput";
  if (type === "FileUpload") return "File";
  if (type === "ImageUpload") return "Image";
  return type || "TextInput";
};

/** Build GroupEditForm Fields[] from pages fieldNodes (label.blocks + id + element). */
const buildGroupEditFieldsFromNodes = (fieldNodes = []) => {
  return (fieldNodes || [])
    .filter((node) => node && node.attribs && node.attribs.name)
    .map((node) => {
      const name = node.attribs.name;
      const labelText =
        node.attribs.label || node.attribs.Label || name;
      const element = mapPagesTypeToGroupEditElement(node.attribs.type);
      const windowName =
        node.attribs.windowname ||
        node.attribs.windowName ||
        node.attribs.value ||
        "";
      const options =
        element === "PickList" || element === "MultiSelect"
          ? extractOptions(node.children || [], "PickList")
          : [];
      return {
        id: name,
        access_mode: "EDITABLE",
        element,
        element_original: element,
        label: { blocks: [{ text: labelText }] },
        // FormBuilder Lookup uses item.value as CRM window name
        value: element === "Lookup" || element === "MultiLookup" ? windowName : "",
        options,
        selectedValue: "",
        displayValue: "",
      };
    });
};

/** Apply pages fill="from:to|..." from a lookup record onto extras. */
const applyFillFromLookupRecord = (fieldNodes, fieldName, source, extras) => {
  if (!source || typeof source !== "object") return;
  const node = (fieldNodes || []).find(
    (item) => item && item.attribs && item.attribs.name === fieldName
  );
  const fill = node && (node.attribs.fill || node.attribs.Fill);
  if (!fill) return;
  String(fill)
    .split("|")
    .forEach((pair) => {
      const parts = pair.split(":");
      const from = parts[0] ? parts[0].trim() : "";
      const to = parts[1] ? parts[1].trim() : "";
      if (!from || !to || source[from] === undefined || source[from] === null) {
        return;
      }
      let raw = source[from];
      if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        raw =
          raw.value != null
            ? raw.value
            : raw.name != null
            ? raw.name
            : "";
      }
      extras[to] = raw;
    });
};

/**
 * Pages-native scm:subform / scm:inputsubform.
 * Columns come from child markup. Rows live on formPayload[name].selected_value.
 * No window / layout fieldGroups required.
 *
 * Attributes:
 *   name (required) — Pages Array variable / form key
 *   loop (optional) — "default" | "custom"
 *   layout (optional) — "rows" (default) | "columns"
 *   mode (optional) — "form" (default) | "detail"
 *     detail: Shipments Detail-style table — view row vs inline edit (Edit/Save/Cancel/Delete)
 *   display (optional) — "table": always-editable add-form grid (checkbox, delete, labeled columns, +)
 *   initialrow (optional) — "true" with display="table" starts one blank row when the array is empty
 *   var  (optional) — row alias in custom mode (default "row")
 */
const ScmSubFormField = ({ attributes, elmtParams }) => {
  const { convertAndRenderElement } = elmtParams;
  const { children = [], dynamicVar: initialDynamicVar, defProps } = elmtParams;

  const formName = attributes?.name || "";
  const loopMode = (attributes?.loop || "default").toLowerCase();
  const layoutMode = String(
    attributes?.layout || attributes?.Layout || "rows"
  ).toLowerCase();
  const viewMode = String(
    attributes?.mode || attributes?.Mode || "form"
  ).toLowerCase();
  const isDetailMode =
    viewMode === "detail" ||
    viewMode === "view" ||
    viewMode === "inline-edit";
  const isColumnsLayout = layoutMode === "columns" || layoutMode === "column";
  const rowVarName = attributes?.var || "row";
  const isWrite = isSet(defProps?.isWrite, isSet(defProps?.DVis_write, true));

  const { onChangeResourcesFieldHandler, fieldFormPayload } = defProps || {};

  const fieldNodes = useMemo(
    () => collectFieldNodesFromChildren(children),
    [children]
  );

  const columnNames = useMemo(
    () =>
      fieldNodes.length
        ? fieldNodes.map((n) => n.attribs.name)
        : collectColumnNamesFromChildren(children),
    [fieldNodes, children]
  );

  const payloadRows = useMemo(
    () => readRowsFromFormPayload(fieldFormPayload, formName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldFormPayload, formName, JSON.stringify(fieldFormPayload?.[formName]?.selected_value)]
  );

  const [rows, setRows] = useState(payloadRows);
  const skipNextPayloadSync = useRef(false);
  const editSnapshotsRef = useRef({});
  // Keep latest rows for nested cell edits — avoids stale closure wiping prior
  // weight/length/width/height when fill or rapid Decimal onChange fires.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  useEffect(() => {
    if (skipNextPayloadSync.current) {
      skipNextPayloadSync.current = false;
      return;
    }
    setRows(payloadRows);
  }, [payloadRows]);

  const commitRows = useCallback(
    (nextRows) => {
      const next = Array.isArray(nextRows) ? nextRows : [];
      skipNextPayloadSync.current = true;
      rowsRef.current = next;
      setRows(next);
      if (typeof onChangeResourcesFieldHandler === "function" && formName) {
        onChangeResourcesFieldHandler(
          { api_name: formName, variable_name: formName },
          next
        );
      }
    },
    [formName, onChangeResourcesFieldHandler]
  );

  const displayMode = String(
    attributes?.display || attributes?.Display || ""
  ).toLowerCase();
  const isTableDisplay = displayMode === "table";
  const wantsInitialRow =
    attributes?.initialrow === true ||
    attributes?.initialrow === "true" ||
    attributes?.initialRow === "true";
  const seededInitialRow = useRef(false);

  useEffect(() => {
    if (!isTableDisplay || !wantsInitialRow || seededInitialRow.current) {
      return;
    }
    if (rows.length > 0) {
      seededInitialRow.current = true;
      return;
    }
    if (!columnNames.length) {
      return;
    }
    seededInitialRow.current = true;
    const defaults = {};
    fieldNodes.forEach((node) => {
      const name = node?.attribs?.name;
      const def = node?.attribs?.defaultvalue || node?.attribs?.defaultValue;
      if (name && def && String(def).indexOf("${") === -1) {
        defaults[name] = def;
      }
    });
    commitRows(addRow([], columnNames, defaults));
  }, [isTableDisplay, wantsInitialRow, rows.length, columnNames, fieldNodes, commitRows]);

  // Package-type driven behaviour (Layout parity, SubformTypeForm): when the
  // package type is Envelope, the Package subform is a single-row variant —
  // no Add/Copy/Delete, no Length/Width/Height columns (live zeroes them).
  const packageTypeRaw =
    fieldFormPayload?.package_type?.selected_value ??
    fieldFormPayload?.["Package / Pallet"]?.selected_value ??
    "";
  const packageTypeValue =
    packageTypeRaw && typeof packageTypeRaw === "object"
      ? packageTypeRaw.value || packageTypeRaw.label || packageTypeRaw.name || ""
      : packageTypeRaw;
  const packageTypeText = String(packageTypeValue || "").trim();
  let envelopeId = "";
  let palletId = "";
  try {
    envelopeId = getHardCodedIds("envelopeId") || "";
    palletId = getHardCodedIds("palletId") || "";
  } catch (e) {
    envelopeId = "";
    palletId = "";
  }
  const isEnvelope =
    packageTypeText.toLowerCase() === "envelope" ||
    (envelopeId !== "" && packageTypeText === envelopeId);
  const isPallet =
    packageTypeText.toLowerCase() === "pallet" ||
    (palletId !== "" && packageTypeText === palletId);
  const isPackageEnvelope =
    isTableDisplay && isEnvelope && /package/i.test(formName || "");

  // Envelope allows exactly one package row: drop extras when it activates.
  useEffect(() => {
    if (!isPackageEnvelope || rows.length <= 1) {
      return;
    }
    const firstRow = { ...rows[0] };
    delete firstRow.is_selected;
    commitRows([firstRow]);
  }, [isPackageEnvelope, rows, commitRows]);

  const handleAdd = useCallback(() => {
    if (isDetailMode) {
      const next = addRowInEdit(rows, columnNames, { exclusive: false });
      const newRow = next[next.length - 1];
      if (newRow?._scmRowId) {
        editSnapshotsRef.current[newRow._scmRowId] = cloneRowSnapshot(newRow);
      }
      commitRows(next);
      return;
    }
    const defaults = {};
    if (isTableDisplay) {
      fieldNodes.forEach((node) => {
        const name = node?.attribs?.name;
        const def = node?.attribs?.defaultvalue || node?.attribs?.defaultValue;
        if (name && def && String(def).indexOf("${") === -1) {
          defaults[name] = def;
        }
      });
    }
    commitRows(addRow(rows, columnNames, defaults));
  }, [commitRows, rows, columnNames, isDetailMode, isTableDisplay, fieldNodes]);

  const handleCopy = useCallback(
    (rowIndex) => {
      commitRows(copyRow(rows, rowIndex));
    },
    [commitRows, rows]
  );

  const handleDelete = useCallback(
    (rowIndex) => {
      const id = rows[rowIndex]?._scmRowId;
      if (id) {
        delete editSnapshotsRef.current[id];
      }
      commitRows(deleteRow(rows, rowIndex));
    },
    [commitRows, rows]
  );

  const handleDeleteSelected = useCallback(() => {
    editSnapshotsRef.current = {};
    commitRows(deleteSelectedRows(rows));
  }, [commitRows, rows]);

  const handleSelectRow = useCallback(
    (rowIndex, selected) => {
      commitRows(setRowSelected(rows, rowIndex, selected));
    },
    [commitRows, rows]
  );

  const handleSelectAll = useCallback(
    (selected) => {
      const checked =
        typeof selected === "boolean"
          ? selected
          : !(rows.length > 0 && rows.every((r) => r.is_selected === true));
      commitRows(setAllRowsSelected(rows, checked));
    },
    [commitRows, rows]
  );

  const handleEditRow = useCallback(
    (rowIndex) => {
      const row = rows[rowIndex];
      if (!row) return;
      if (row.isOpenBox !== true && row._scmRowId) {
        editSnapshotsRef.current[row._scmRowId] = cloneRowSnapshot(row);
      }
      commitRows(beginRowEdit(rows, rowIndex, { exclusive: false }));
    },
    [commitRows, rows]
  );

  /** Inline ✓ : keep this row, revert other open rows, close all to view. */
  const handleSaveRow = useCallback(
    (rowIndex) => {
      const next = saveOneRowRevertOthers(
        rows,
        rowIndex,
        editSnapshotsRef.current
      );
      editSnapshotsRef.current = {};
      commitRows(next);
    },
    [commitRows, rows]
  );

  /** Bulk SAVE: keep all current values, close all to view. */
  const handleBulkSave = useCallback(() => {
    editSnapshotsRef.current = {};
    commitRows(saveAllOpenRows(rows));
  }, [commitRows, rows]);

  const handleCancelRow = useCallback(
    (rowIndex) => {
      const current = rows[rowIndex];
      const id = current?._scmRowId;
      const snap = id ? editSnapshotsRef.current[id] : null;
      if (id) {
        delete editSnapshotsRef.current[id];
      }
      if (current?._isNew) {
        commitRows(deleteRow(rows, rowIndex));
        return;
      }
      if (snap) {
        const next = [...rows];
        const restored = { ...snap, isOpenBox: false, _scmRowId: id };
        delete restored._isNew;
        next[rowIndex] = restored;
        commitRows(next);
        return;
      }
      commitRows(endRowEdit(rows, rowIndex));
    },
    [commitRows, rows]
  );

  const handleNestedInputChange = useCallback(
    (fieldName, value, rowIndex) => {
      let normalized = value;
      const extras = {};
      if (
        normalized !== null &&
        typeof normalized === "object" &&
        !Array.isArray(normalized) &&
        normalized.lookupData &&
        typeof normalized.lookupData === "object"
      ) {
        const source = normalized.lookupData;
        const node = fieldNodes.find(
          (item) => item && item.attribs && item.attribs.name === fieldName
        );
        const fill = node && (node.attribs.fill || node.attribs.Fill);
        if (fill) {
          String(fill).split("|").forEach((pair) => {
            const parts = pair.split(":");
            const from = parts[0] ? parts[0].trim() : "";
            const to = parts[1] ? parts[1].trim() : "";
            if (!from || !to || source[from] === undefined || source[from] === null) {
              return;
            }
            let raw = source[from];
            if (raw && typeof raw === "object" && !Array.isArray(raw)) {
              raw = raw.value != null ? raw.value : raw.name != null ? raw.name : "";
            }
            extras[to] = raw;
          });
        }
        const stored = { ...normalized };
        delete stored.lookupData;
        normalized = stored;
      } else if (
        normalized !== null &&
        typeof normalized === "object" &&
        !Array.isArray(normalized) &&
        "value" in normalized &&
        !("label" in normalized)
      ) {
        normalized = normalized.value;
      }
      let next = updateRowField(rowsRef.current, rowIndex, fieldName, normalized);
      Object.keys(extras).forEach((key) => {
        next = updateRowField(next, rowIndex, key, extras[key]);
      });
      commitRows(next);
    },
    [commitRows, fieldNodes]
  );

  const selectedCount = rows.filter((r) => r.is_selected === true).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const editingCount = rows.filter((r) => r.isOpenBox === true).length;
  const showBulkSave = isWrite && editingCount > 1;
  const prevOpenIdsRef = useRef(new Set());
  const prevRowCountRef = useRef(rows.length);

  // Scroll the row/column that just opened into view. Do not scroll on field edits.
  useEffect(() => {
    const openIds = rows
      .filter((row) => row.isOpenBox === true && row._scmRowId)
      .map((row) => row._scmRowId);
    const newlyOpened = openIds.filter((id) => !prevOpenIdsRef.current.has(id));
    prevOpenIdsRef.current = new Set(openIds);

    let targetId = newlyOpened.length
      ? newlyOpened[newlyOpened.length - 1]
      : null;
    if (
      !targetId &&
      isColumnsLayout &&
      rows.length > prevRowCountRef.current
    ) {
      targetId = rows[rows.length - 1]?._scmRowId || null;
    }
    prevRowCountRef.current = rows.length;
    if (!targetId) {
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-scm-subform-scroll="${targetId}"]`
      );
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [rows, isColumnsLayout]);

  // Group Edit (layout SubformTypeForm pattern): header pencil → modal when ≥2 selected
  const groupEditFields = useMemo(
    () => buildGroupEditFieldsFromNodes(fieldNodes),
    [fieldNodes]
  );
  const [groupEditOpen, setGroupEditOpen] = useState(false);
  const [groupEditPayload, setGroupEditPayload] = useState({
    type: "",
    value: "",
  });

  const closeGroupEdit = useCallback((open = false) => {
    setGroupEditOpen(open === true);
    setGroupEditPayload({ type: "", value: "" });
  }, []);

  const openGroupEdit = useCallback(() => {
    setGroupEditPayload({ type: "", value: "" });
    setGroupEditOpen(true);
  }, []);

  const updateGroupEditPayload = useCallback((updatedVal) => {
    setGroupEditPayload(updatedVal || { type: "", value: "" });
  }, []);

  /** Pages Group Edit Value change — keep lookupData so fill runs on save. */
  const handleGroupEditValueChange = useCallback(
    (fieldName, value) => {
      const node = fieldNodes.find(
        (item) => item && item.attribs && item.attribs.name === fieldName
      );
      const rawType = String((node && node.attribs && node.attribs.type) || "");
      let inputType = "text";
      if (rawType === "Lookup" || rawType === "MultiLookup") {
        inputType = "Lookup";
      } else if (rawType === "PickList" || rawType === "Picklist") {
        inputType = "PickList";
      } else if (
        rawType === "Decimal" ||
        rawType === "Number" ||
        rawType === "LongNumber" ||
        rawType === "Currency" ||
        rawType === "Percentage"
      ) {
        inputType = "number";
      } else if (rawType === "Date" || rawType === "DateTime") {
        inputType = "date";
      }

      let stored = value;
      if (
        stored !== null &&
        typeof stored === "object" &&
        !Array.isArray(stored) &&
        "value" in stored &&
        !("label" in stored) &&
        stored.lookupData == null &&
        stored.selectedValue === undefined &&
        stored.displayValue === undefined
      ) {
        stored = stored.value;
      }

      setGroupEditPayload((prev) => ({
        ...prev,
        type: fieldName || prev.type,
        value: stored,
        inputType,
        updateData:
          stored &&
          typeof stored === "object" &&
          stored.lookupData
            ? { lookupData: stored.lookupData }
            : null,
      }));
    },
    [fieldNodes]
  );

  const saveGroupEdit = useCallback(() => {
    const fieldName = groupEditPayload.type;
    if (!fieldName) {
      closeGroupEdit(false);
      return;
    }
    const inputType = groupEditPayload.inputType || "text";
    const fieldValue = groupEditPayload.value;
    const updateData = groupEditPayload.updateData;
    const extras = {};
    let normalized = fieldValue;

    const sourceFromValue =
      fieldValue &&
      typeof fieldValue === "object" &&
      fieldValue.lookupData
        ? fieldValue.lookupData
        : null;
    const source =
      (updateData && updateData.lookupData) || sourceFromValue || null;

    const isLookupLike =
      inputType === "Lookup" ||
      (fieldValue &&
        typeof fieldValue === "object" &&
        !Array.isArray(fieldValue) &&
        (fieldValue.lookupData != null ||
          fieldValue.label != null ||
          fieldValue.displayValue != null ||
          fieldValue.selectedValue != null));

    if (isLookupLike && fieldValue && typeof fieldValue === "object") {
      // Same fill as cell-level Packages selection (fill="cf_weight:weight|...")
      applyFillFromLookupRecord(fieldNodes, fieldName, source, extras);
      normalized = {
        label:
          fieldValue.displayValue != null
            ? fieldValue.displayValue
            : fieldValue.label != null
            ? fieldValue.label
            : "",
        value:
          fieldValue.selectedValue != null
            ? fieldValue.selectedValue
            : fieldValue.value != null
            ? fieldValue.value
            : "",
      };
    } else if (
      normalized !== null &&
      typeof normalized === "object" &&
      !Array.isArray(normalized) &&
      "value" in normalized &&
      !("label" in normalized) &&
      normalized.selectedValue === undefined
    ) {
      normalized = normalized.value;
    }

    const next = rows.map((row) => {
      if (row.is_selected !== true) {
        return row;
      }
      let updated = { ...row, [fieldName]: normalized, is_selected: false };
      Object.keys(extras).forEach((key) => {
        updated[key] = extras[key];
      });
      return updated;
    });
    commitRows(next);
    closeGroupEdit(false);
  }, [
    groupEditPayload,
    rows,
    fieldNodes,
    commitRows,
    closeGroupEdit,
  ]);

  const sectionActions = useMemo(
    () => ({
      add: {
        onClick: handleAdd,
        type: "add",
        access_mode: isWrite,
      },
      edit: {
        onClick: openGroupEdit,
        type: "BulkEdit",
        access_mode: isWrite && selectedCount > 1,
      },
      delete: {
        onClick: handleDeleteSelected,
        type: "BulkDelete",
        access_mode: isWrite && selectedCount > 1,
      },
      save: {
        onClick: handleBulkSave,
        type: "BulkSave",
        access_mode: showBulkSave,
      },
    }),
    [
      handleAdd,
      openGroupEdit,
      handleDeleteSelected,
      handleBulkSave,
      isWrite,
      selectedCount,
      showBulkSave,
      editingCount,
    ]
  );

  const sectionCheckbox = useMemo(
    () => ({
      selectAll: {
        type: "CheckBoxes",
        element: "CheckBoxes",
        checked: allSelected,
        selectedValue: allSelected,
        access_mode: isWrite && rows.length > 1,
        withoutBlock: true,
        onChangeInputAction: (_field, e) => {
          const checked = e?.target ? e.target.checked : !allSelected;
          handleSelectAll(checked);
        },
      },
    }),
    [allSelected, handleSelectAll, isWrite, rows.length]
  );

  const enrichedRows = useMemo(() => {
    return rows.map((row, rowIndex) => {
      const editing = row.isOpenBox === true;
      return {
        ...row,
        loopIndex: rowIndex,
        isOpenBox: editing,
        actions: {
          clone: {
            onClick: () => handleCopy(rowIndex),
            type: "copy",
            access_mode: isWrite && !editing,
          },
          delete: {
            onClick: () => handleDelete(rowIndex),
            type: "delete",
            access_mode: isWrite && !editing,
          },
          edit: {
            onClick: () => handleEditRow(rowIndex),
            type: "edit",
            access_mode: isWrite && !editing,
          },
          save: {
            onClick: () => handleSaveRow(rowIndex),
            type: "save",
            access_mode: isWrite && editing,
          },
          cancel: {
            onClick: () => handleCancelRow(rowIndex),
            type: "cancel",
            access_mode: isWrite && editing,
          },
        },
        checkbox: {
          selected: {
            type: "CheckBoxes",
            element: "CheckBoxes",
            checked: row.is_selected === true,
            selectedValue: row.is_selected === true,
            access_mode: isWrite && rows.length > 1 && !editing,
            withoutBlock: true,
            is_subform_field: true,
            onChangeInputAction: (_field, e) => {
              const checked = e?.target ? e.target.checked : !row.is_selected;
              handleSelectRow(rowIndex, checked);
            },
          },
        },
      };
    });
  }, [
    rows,
    handleCopy,
    handleDelete,
    handleSelectRow,
    handleEditRow,
    handleSaveRow,
    handleCancelRow,
    isWrite,
  ]);

  const pagesDynamicVar = useMemo(() => {
    return {
      ...initialDynamicVar,
      [formName]: {
        ...(initialDynamicVar?.[formName] || {}),
        rows: enrichedRows,
        actions: sectionActions,
        checkbox: sectionCheckbox,
      },
    };
  }, [
    initialDynamicVar,
    formName,
    enrichedRows,
    sectionActions,
    sectionCheckbox,
  ]);

  // Page-level field validation: a step/config output named "<field>_error" flags the
  // field after a save attempt; in table mode it is shown under that field's input only
  // while this row's value is still blank (incl. decimal placeholder zeros 0 / 0.00),
  // so it clears live as the user types a real value.
  // (unwrap convention mirrors ScmDecisionMaking).
  const isBlankishSubformCellValue = (raw) => {
    if (raw === null || raw === undefined) {
      return true;
    }
    if (typeof raw === "object" && !Array.isArray(raw)) {
      if (raw.selected_value !== undefined) {
        return isBlankishSubformCellValue(raw.selected_value);
      }
      if (raw.value !== undefined) {
        return isBlankishSubformCellValue(raw.value);
      }
    }
    const s = String(raw).trim();
    if (s === "" || s === "-None-" || s === "null" || s.toLowerCase() === "undefined") {
      return true;
    }
    // Package Decimal cells often keep placeholder-like "0" / "0.00" as the stored
    // value; treat those as empty so required *_error messages still render.
    if (/^[-+]?0+(?:\.0+)?$/.test(s)) {
      return true;
    }
    return false;
  };

  const renderFieldError = (fieldNode, rowData) => {
    const fieldName = fieldNode?.attribs?.name || "";
    if (!fieldName) {
      return null;
    }
    const errorKey = `${fieldName}_error`;
    // Same sources ScmInputTextField merges for standalone fields: dynamicVar,
    // execute outputResponse, and live formPayload (selected_value wrappers).
    let errVal = extractFieldValue(pagesDynamicVar, errorKey);
    if (errVal == null || errVal === "" || String(errVal) === "null") {
      errVal = extractFieldValue(defProps?.outputResponse || {}, errorKey);
    }
    if (errVal == null || errVal === "" || String(errVal) === "null") {
      const fromPayload = fieldFormPayload?.[errorKey];
      if (fromPayload != null && typeof fromPayload === "object" && !Array.isArray(fromPayload)) {
        errVal =
          fromPayload.selected_value !== undefined
            ? fromPayload.selected_value
            : fromPayload.value;
      } else if (fromPayload != null) {
        errVal = fromPayload;
      }
    }
    if (errVal && typeof errVal === "object" && !Array.isArray(errVal)) {
      if (errVal.selected_value !== undefined) {
        errVal = errVal.selected_value;
      } else if (errVal.value !== undefined) {
        errVal = errVal.value;
      }
    }
    if (errVal === null || errVal === undefined || String(errVal).trim() === "" || String(errVal) === "null") {
      return null;
    }
    const rowVal = rowData ? rowData[fieldName] : undefined;
    if (!isBlankishSubformCellValue(rowVal)) {
      return null;
    }
    return <div className="text-danger small mt-1 val-err">{String(errVal)}</div>;
  };

  const renderMarkupNodes = useCallback(
    (nodes, rowData, rowIndex, keyPrefix = "n") => {
      const rowDynamicVar = {
        ...pagesDynamicVar,
        [rowVarName]: { ...rowData, loopIndex: rowIndex },
      };
      const rowElmtParams = {
        ...elmtParams,
        scmVarName: rowVarName,
        dynamicVar: rowDynamicVar,
      };

      return (nodes || []).map((child, childIndex) => {
        const tag = String(child?.name || "").toLowerCase();
        const nodeKey = `${formName}_${keyPrefix}_${rowIndex}_${childIndex}`;

        // Recursive scm:div — horizontal / vertical / nested layouts
        if (tag === "scm:div") {
          const divChildren = isSet(child.children, []);
          const rawDivStyle = isSet(child.attribs?.style, "");
          const divStyle =
            typeof rawDivStyle === "string"
              ? extractStylesAsObjects(rawDivStyle)
              : rawDivStyle;
          const divClassName = isSet(child.attribs?.class, "");
          const divId = isSet(child.attribs?.id, undefined);
          return (
            <div
              key={nodeKey}
              id={divId}
              data-scm-subform-div="true"
              style={divStyle}
              className={divClassName}
            >
              {renderMarkupNodes(
                divChildren,
                rowData,
                rowIndex,
                `${keyPrefix}_${childIndex}`
              )}
            </div>
          );
        }

        if (tag === "scm:inputtext") {
          return (
            <ScmFormInputWrapper
              key={nodeKey}
              inputChild={child}
              formName={formName}
              divIndex={rowIndex}
              formData={rows}
              handleNestedInputChange={handleNestedInputChange}
              elmtParams={rowElmtParams}
              defProps={defProps}
              layoutCell={true}
            />
          );
        }

        // Plain HTML wrappers (div/span/label) from markup parser
        if (tag && !tag.startsWith("scm:")) {
          const rawStyle = isSet(child.attribs?.style, "");
          const style =
            typeof rawStyle === "string"
              ? extractStylesAsObjects(rawStyle)
              : rawStyle;
          const className = isSet(child.attribs?.class, "");
          const {
            style: _s,
            class: _c,
            className: _cn,
            ...htmlAttribs
          } = child.attribs || {};
          const htmlChildren = isSet(child.children, []);
          if (htmlChildren.length === 0) {
            return React.createElement(tag, {
              key: nodeKey,
              ...htmlAttribs,
              className,
              style,
            });
          }
          return React.createElement(
            tag,
            { key: nodeKey, ...htmlAttribs, className, style },
            renderMarkupNodes(
              htmlChildren,
              rowData,
              rowIndex,
              `${keyPrefix}_${childIndex}`
            )
          );
        }

        return (
          <React.Fragment key={nodeKey}>
            {convertAndRenderElement([child], {
              ...rowElmtParams,
              formScope: formName,
            })}
          </React.Fragment>
        );
      });
    },
    [
      formName,
      rows,
      handleNestedInputChange,
      elmtParams,
      rowVarName,
      pagesDynamicVar,
      defProps,
      convertAndRenderElement,
    ]
  );

  const renderDefaultRowChildren = useCallback(
    (rowData, rowIndex) => renderMarkupNodes(children || [], rowData, rowIndex, "root"),
    [children, renderMarkupNodes]
  );

  if (!formName) {
    return null;
  }

  // Custom loop: designer markup; inject name.rows / name.actions / name.checkbox
  if (loopMode === "custom") {
    return (
      <div
        data-scm-subform={formName}
        data-scm-subform-mode="custom"
        className={attributes?.class || attributes?.className || ""}
      >
        {convertAndRenderElement(children || [], {
          ...elmtParams,
          dynamicVar: pagesDynamicVar,
          formScope: formName,
        })}
      </div>
    );
  }

  // Add-form table: checkbox, delete, labeled columns, plus. Cells stay editable.
  if (isTableDisplay) {
    const showRowSelect = !isPackageEnvelope && rows.length > 1;
    const fewSelected = selectedCount <= 1;
    // Envelope shows only Packages / Weight / UOM / Description (no dimensions).
    const visibleFieldNodes = isPackageEnvelope
      ? fieldNodes.filter((fieldNode) => {
          const name = String(fieldNode?.attribs?.name || "").toLowerCase();
          return name !== "length" && name !== "width" && name !== "height";
        })
      : fieldNodes;
    // Layout: Description is mandatory only for Pallet.
    const compactField = (fieldNode) => {
      const name = String(fieldNode?.attribs?.name || "").toLowerCase();
      const attribs = {
        ...(fieldNode.attribs || {}),
        withlabel: "false",
        inputOnly: "true",
        class: fieldNode.attribs?.class || "form-control",
      };
      if (name === "description") {
        if (isPallet) {
          attribs.required = "true";
        } else {
          delete attribs.required;
        }
      }
      return { ...fieldNode, attribs };
    };
    const showAddButton = isWrite && !isEnvelope && selectedCount === 0;
    const showGroupEditDelete = isWrite && selectedCount > 1;
    const addSlot =
      typeof document !== "undefined"
        ? document.querySelector(`[data-scm-subform-add="${formName}"]`)
        : null;
    // Layout SectionFormHeader: Edit/Delete centered over the section header bar
    let groupEditCenterSlot =
      typeof document !== "undefined"
        ? document.querySelector(`[data-scm-subform-group-edit="${formName}"]`)
        : null;
    if (!groupEditCenterSlot && typeof document !== "undefined") {
      const headerEl =
        (addSlot && addSlot.closest(".card-header")) ||
        (addSlot && addSlot.parentElement) ||
        null;
      if (headerEl) {
        if (
          typeof window !== "undefined" &&
          window.getComputedStyle(headerEl).position === "static"
        ) {
          headerEl.style.position = "relative";
        }
        groupEditCenterSlot = document.createElement("div");
        groupEditCenterSlot.setAttribute(
          "data-scm-subform-group-edit",
          formName
        );
        groupEditCenterSlot.className =
          "pkg-group-edit-slot d-flex align-items-center justify-content-center";
        groupEditCenterSlot.style.cssText =
          "position:absolute;top:3px;left:0;right:0;margin:0 auto;z-index:2;pointer-events:none;";
        headerEl.appendChild(groupEditCenterSlot);
      }
    }
    const addButton = showAddButton ? (
      <a
        className="btn"
        href="#"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.nativeEvent && typeof event.nativeEvent.stopImmediatePropagation === "function") {
            event.nativeEvent.stopImmediatePropagation();
          }
          handleAdd();
        }}
        title="Add"
      >
        <i className="fas fa-plus primary_color" />
      </a>
    ) : null;
    // Layout SubformTypeForm: Edit + Delete when ≥2 rows selected (header pencil → Group Edit)
    const groupHeaderButtons = showGroupEditDelete ? (
      <div className="btn-group" style={{ pointerEvents: "auto" }}>
        <Buttons
          buttonList={[
            {
              clickHandler: openGroupEdit,
              type: "edit",
              attributes: { className: "btn border-default btn-border" },
              svgIcon: "Edit",
              access_mode: true,
            },
            {
              clickHandler: handleDeleteSelected,
              type: "delete",
              attributes: { className: "btn border-default btn-border" },
              svgIcon: "Delete",
              title: "delete",
              access_mode: true,
            },
          ]}
        />
      </div>
    ) : null;
    // overflow must stay visible so Packages lookup autocomplete can float
    // above rows/sections (overflow-x:auto would clip it like a nested scroll box).
    return (
      <div
        data-scm-subform={formName}
        data-scm-subform-mode="table"
        className={attributes?.class || attributes?.className || ""}
        style={{ overflow: "visible", position: "relative", zIndex: 100 }}
      >
        {groupEditCenterSlot
          ? ReactDOM.createPortal(groupHeaderButtons, groupEditCenterSlot)
          : null}
        {addSlot
          ? ReactDOM.createPortal(addButton, addSlot)
          : showAddButton && (
              <div className="card-tools float-right" style={{ position: "relative", zIndex: 1 }}>
                {addButton}
              </div>
            )}
        {groupEditOpen && (
          <PagesGroupEditForm
            closePopupToogle={closeGroupEdit}
            saveHandler={saveGroupEdit}
            Fields={groupEditFields}
            updatePayload={updateGroupEditPayload}
            payload={groupEditPayload}
            fieldNodes={fieldNodes}
            formName={formName}
            defProps={defProps}
            elmtParams={elmtParams}
            rowVarName={rowVarName}
            onValueChange={handleGroupEditValueChange}
          />
        )}
        <div className="table_Body p-0 nowrap" style={{ overflow: "visible" }}>
        <table className="table mb-0" style={{ overflow: "visible", position: "relative" }}>
          <thead>
            <tr>
              {isWrite && (
                <th className="fixed-first-col" style={{ width: 30, position: "sticky", left: 0, backgroundColor: "white", zIndex: 2 }}>
                  {showRowSelect && (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      title="Select all"
                    />
                  )}
                </th>
              )}
              {visibleFieldNodes.map((fieldNode) => {
                const fieldName = fieldNode?.attribs?.name || "";
                const header =
                  fieldNode?.attribs?.label ||
                  fieldNode?.attribs?.Label ||
                  fieldName;
                return <th key={`${formName}_h_${fieldName}`}>{header}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((rowData, rowIndex) => (
              <tr
                key={rowData._scmRowId || `${formName}_table_${rowIndex}`}
                data-scm-subform-row={`${formName}_${rowIndex}`}
              >
                {isWrite && (
                  <td className="fixed-first-col" style={{ verticalAlign: "middle", position: "sticky", left: 0, zIndex: 1, backgroundColor: "white" }}>
                    {showRowSelect && (
                      <input
                        type="checkbox"
                        checked={rowData.is_selected === true}
                        onChange={(e) => handleSelectRow(rowIndex, e.target.checked)}
                        title="Select row"
                      />
                    )}
                    <Buttons
                      buttonList={[
                        {
                          clickHandler: () => handleCopy(rowIndex),
                          type: "copy",
                          attributes: { className: "p-1" },
                          svgIcon: "Copy",
                          access_mode: !isPackageEnvelope && fewSelected,
                        },
                        {
                          clickHandler: () => handleDelete(rowIndex),
                          type: "delete",
                          attributes: { className: "p-1" },
                          svgIcon: "Delete",
                          access_mode: showRowSelect && fewSelected,
                        },
                      ]}
                    />
                  </td>
                )}
                {visibleFieldNodes.map((fieldNode, fieldIndex) => {
                  const fieldName = fieldNode?.attribs?.name || `field_${fieldIndex}`;
                  return (
                    <td
                      key={`${formName}_${fieldName}_${rowIndex}`}
                      style={{
                        verticalAlign: "top",
                        minWidth: 110,
                        overflow: "visible",
                        position: "relative",
                      }}
                    >
                      <ScmFormInputWrapper
                        inputChild={compactField(fieldNode)}
                        formName={formName}
                        divIndex={rowIndex}
                        formData={rows}
                        handleNestedInputChange={handleNestedInputChange}
                        elmtParams={{
                          ...elmtParams,
                          scmVarName: rowVarName,
                          dynamicVar: {
                            ...pagesDynamicVar,
                            [rowVarName]: {
                              ...rowData,
                              loopIndex: rowIndex,
                            },
                          },
                        }}
                        defProps={defProps}
                        layoutCell={false}
                      />
                      {renderFieldError(fieldNode, rowData)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
  }

  const toolbar = isWrite && (
    <div
      className="d-flex align-items-center mb-2"
      style={{ gap: 8 }}
      data-scm-subform-toolbar={formName}
    >
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={handleAdd}
      >
        {isDetailMode ? "+" : "Add"}
      </button>
      {!isDetailMode && rows.length > 1 && (
        <>
          <label className="d-flex align-items-center mb-0" style={{ gap: 4 }}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            Select All
          </label>
          {selectedCount > 0 && (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleDeleteSelected}
            >
              Delete selected
            </button>
          )}
        </>
      )}
    </div>
  );

  // Detail mode: Shipments Detail-style table (view vs inline edit)
  if (isDetailMode) {
    return (
      <div
        data-scm-subform={formName}
        data-scm-subform-mode="detail"
        className={attributes?.class || attributes?.className || ""}
        style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
      >
        <div
          className="d-flex align-items-center justify-content-between mb-2"
          style={{ gap: 8 }}
        >
          <h6 className="mb-0">{formName}</h6>
          {isWrite && (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              {showBulkSave && (
                <button
                  type="button"
                  className="btn btn-warning btn-sm"
                  onClick={handleBulkSave}
                  title="Save all rows in edit mode"
                >
                  SAVE
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAdd}
                title="Add row"
              >
                <Icons iconType="AddPlus" size={14} color="#fff" />
              </button>
            </div>
          )}
        </div>

        <div
          data-scm-subform-scrollport="detail"
          style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto" }}
        >
        {rows.length === 0 && (
          <div className="text-muted small mb-2" data-scm-subform-empty="true">
            No records. Click + to add.
          </div>
        )}

        {rows.length > 0 && (
          <table
            className="table table-sm table-striped mb-0"
            style={{ width: "max-content", minWidth: "100%" }}
          >
            <thead>
              <tr>
                {isWrite && <th style={{ width: 88 }}> </th>}
                {fieldNodes.map((fieldNode) => {
                  const fieldName = fieldNode?.attribs?.name || "";
                  return (
                    <th key={`${formName}_h_${fieldName}`}>{fieldName}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((rowData, rowIndex) => {
                const editing = rowData.isOpenBox === true;
                return (
                  <tr
                    key={rowData._scmRowId || `${formName}_detail_${rowIndex}`}
                    data-scm-subform-row={`${formName}_${rowIndex}`}
                    data-scm-subform-scroll={rowData._scmRowId || undefined}
                    data-scm-editing={editing ? "true" : "false"}
                  >
                    {isWrite && (
                      <td style={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
                        {editing ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-success p-0 mr-2"
                              onClick={() => handleSaveRow(rowIndex)}
                              title="Save this row only (revert other open rows)"
                            >
                              <Icons iconType="Tick" size={16} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0"
                              onClick={() => handleCancelRow(rowIndex)}
                              title="Cancel"
                            >
                              <Icons iconType="Cross" size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-primary p-0 mr-2"
                              onClick={() => handleEditRow(rowIndex)}
                              title="Edit"
                            >
                              <Icons iconType="Edit" size={16} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0"
                              onClick={() => handleDelete(rowIndex)}
                              title="Delete"
                            >
                              <Icons iconType="Delete" size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    )}
                    {fieldNodes.map((fieldNode, fieldIndex) => {
                      const fieldName =
                        fieldNode?.attribs?.name || `field_${fieldIndex}`;
                      if (!editing) {
                        return (
                          <td
                            key={`${formName}_${fieldName}_${rowIndex}_view`}
                            style={{ verticalAlign: "middle" }}
                          >
                            {formatCellDisplayValue(rowData[fieldName])}
                          </td>
                        );
                      }
                      const compactChild = {
                        ...fieldNode,
                        attribs: {
                          ...(fieldNode.attribs || {}),
                          withlabel: "false",
                          inputOnly: "true",
                          class: fieldNode.attribs?.class || "form-control",
                        },
                      };
                      return (
                        <td
                          key={`${formName}_${fieldName}_${rowIndex}_edit`}
                          style={{ verticalAlign: "middle", minWidth: 120 }}
                        >
                          <ScmFormInputWrapper
                            inputChild={compactChild}
                            formName={formName}
                            divIndex={rowIndex}
                            formData={rows}
                            handleNestedInputChange={handleNestedInputChange}
                            elmtParams={{
                              ...elmtParams,
                              scmVarName: rowVarName,
                              dynamicVar: {
                                ...pagesDynamicVar,
                                [rowVarName]: {
                                  ...rowData,
                                  loopIndex: rowIndex,
                                },
                              },
                            }}
                            defProps={defProps}
                            layoutCell={false}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>
    );
  }

  // Columns layout: field labels on the left; each Add adds a value column
  if (isColumnsLayout) {
    return (
      <div
        data-scm-subform={formName}
        data-scm-subform-mode="columns"
        className={attributes?.class || attributes?.className || ""}
        style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
      >
        <div
          className="d-flex align-items-center justify-content-between mb-2"
          style={{ gap: 8 }}
        >
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <h6 className="mb-0">{formName}</h6>
            {isWrite && rows.length > 1 && (
              <label className="d-flex align-items-center mb-0" style={{ gap: 4 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                Select All
              </label>
            )}
          </div>
          {isWrite && (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              {selectedCount > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={handleDeleteSelected}
                  title="Delete selected"
                >
                  <Icons iconType="Delete" size={16} />
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAdd}
                title="Add column"
              >
                <Icons iconType="AddPlus" size={14} color="#fff" />
              </button>
            </div>
          )}
        </div>

        {rows.length === 0 && (
          <div className="text-muted small mb-2" data-scm-subform-empty="true">
            No columns yet. Click Add to create one.
          </div>
        )}

        <div
          data-scm-subform-scrollport="columns"
          style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto" }}
        >
        <table
          className="table table-borderless mb-0"
          style={{ width: "max-content", minWidth: "100%" }}
        >
          <thead>
            <tr>
              <th style={{ minWidth: 120 }} />
              {rows.map((rowData, colIndex) => (
                <th
                  key={`${formName}_colhead_${colIndex}`}
                  data-scm-subform-scroll={rowData._scmRowId || undefined}
                  style={{ minWidth: 140, verticalAlign: "top" }}
                >
                  <div className="d-flex flex-column" style={{ gap: 6 }}>
                    {isWrite && (
                      <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        {rows.length > 1 && (
                          <input
                            type="checkbox"
                            checked={rowData.is_selected === true}
                            onChange={(e) =>
                              handleSelectRow(colIndex, e.target.checked)
                            }
                            title="Select column"
                          />
                        )}
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => handleCopy(colIndex)}
                          title="Copy column"
                        >
                          <Icons iconType="Copy" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => handleDelete(colIndex)}
                          title="Delete column"
                        >
                          <Icons iconType="Delete" size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fieldNodes.map((fieldNode, fieldIndex) => {
              const fieldName = fieldNode?.attribs?.name || `field_${fieldIndex}`;
              const compactChild = {
                ...fieldNode,
                attribs: {
                  ...(fieldNode.attribs || {}),
                  withlabel: "false",
                  inputOnly: "true",
                  class: fieldNode.attribs?.class || "form-control",
                },
              };
              return (
                <tr key={`${formName}_field_${fieldName}`}>
                  <td
                    style={{
                      fontWeight: 600,
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                      paddingRight: 12,
                    }}
                  >
                    {fieldName}
                  </td>
                  {rows.map((rowData, colIndex) => (
                    <td
                      key={`${formName}_${fieldName}_${colIndex}`}
                      style={{ verticalAlign: "middle", minWidth: 140 }}
                    >
                      <ScmFormInputWrapper
                        inputChild={compactChild}
                        formName={formName}
                        divIndex={colIndex}
                        formData={rows}
                        handleNestedInputChange={handleNestedInputChange}
                        elmtParams={{
                          ...elmtParams,
                          scmVarName: rowVarName,
                          dynamicVar: {
                            ...pagesDynamicVar,
                            [rowVarName]: {
                              ...rowData,
                              loopIndex: colIndex,
                            },
                          },
                        }}
                        defProps={defProps}
                        layoutCell={false}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    );
  }

  const hasCustomDivLayout = (children || []).some(
    (child) => String(child?.name || "").toLowerCase() === "scm:div"
  );

  // Flat fields: Add Shipment-style table. scm:div keeps the custom flex layout.
  if (!hasCustomDivLayout) {
    return (
      <div
        data-scm-subform={formName}
        data-scm-subform-mode="form"
        className={attributes?.class || attributes?.className || ""}
        style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
      >
        <div
          className="d-flex align-items-center justify-content-between mb-2"
          style={{ gap: 8 }}
        >
          <h6 className="mb-0">{formName}</h6>
          {isWrite && (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              {selectedCount > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={handleDeleteSelected}
                  title="Delete selected"
                >
                  <Icons iconType="Delete" size={16} />
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAdd}
                title="Add row"
              >
                <Icons iconType="AddPlus" size={14} color="#fff" />
              </button>
            </div>
          )}
        </div>

        <div
          data-scm-subform-scrollport="form"
          style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto" }}
        >
          {rows.length === 0 && (
            <div className="text-muted small mb-2" data-scm-subform-empty="true">
              No rows yet. Click + to add.
            </div>
          )}
          <table
            className="table table-sm mb-0"
            style={{ width: "max-content", minWidth: "100%" }}
          >
            <thead>
              <tr>
                {isWrite && (
                  <th style={{ width: 36 }}>
                    {rows.length > 1 && (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        title="Select all"
                      />
                    )}
                  </th>
                )}
                {isWrite && <th style={{ width: 72 }}> </th>}
                {fieldNodes.map((fieldNode) => {
                  const fieldName = fieldNode?.attribs?.name || "";
                  return <th key={`${formName}_h_${fieldName}`}>{fieldName}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((rowData, rowIndex) => (
                <tr
                  key={rowData._scmRowId || `${formName}_form_${rowIndex}`}
                  data-scm-subform-row={`${formName}_${rowIndex}`}
                >
                  {isWrite && (
                    <td style={{ verticalAlign: "middle" }}>
                      <input
                        type="checkbox"
                        checked={rowData.is_selected === true}
                        onChange={(e) => handleSelectRow(rowIndex, e.target.checked)}
                        title="Select row"
                      />
                    </td>
                  )}
                  {isWrite && (
                    <td style={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 mr-2"
                        onClick={() => handleCopy(rowIndex)}
                        title="Copy"
                      >
                        <Icons iconType="Copy" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => handleDelete(rowIndex)}
                        title="Delete"
                      >
                        <Icons iconType="Delete" size={16} />
                      </button>
                    </td>
                  )}
                  {fieldNodes.map((fieldNode, fieldIndex) => {
                    const fieldName =
                      fieldNode?.attribs?.name || `field_${fieldIndex}`;
                    const compactChild = {
                      ...fieldNode,
                      attribs: {
                        ...(fieldNode.attribs || {}),
                        withlabel: "false",
                        inputOnly: "true",
                        class: fieldNode.attribs?.class || "form-control",
                      },
                    };
                    return (
                      <td
                        key={`${formName}_${fieldName}_${rowIndex}`}
                        style={{ verticalAlign: "middle", minWidth: 120 }}
                      >
                        <ScmFormInputWrapper
                          inputChild={compactChild}
                          formName={formName}
                          divIndex={rowIndex}
                          formData={rows}
                          handleNestedInputChange={handleNestedInputChange}
                          elmtParams={{
                            ...elmtParams,
                            scmVarName: rowVarName,
                            dynamicVar: {
                              ...pagesDynamicVar,
                              [rowVarName]: {
                                ...rowData,
                                loopIndex: rowIndex,
                              },
                            },
                          }}
                          defProps={defProps}
                          layoutCell={false}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Custom scm:div layout: Add / Select All / per-row Copy+Delete around the designer's markup
  return (
    <div
      data-scm-subform={formName}
      data-scm-subform-mode="default"
      className={attributes?.class || attributes?.className || ""}
    >
      {toolbar}

      {rows.length === 0 && (
        <div className="text-muted small mb-2" data-scm-subform-empty="true">
          No rows yet. Click Add to create one.
        </div>
      )}

      {rows.map((rowData, rowIndex) => (
        <div
          key={`${formName}_row_${rowIndex}`}
          className="d-flex align-items-start mb-2"
          style={{ gap: 8 }}
          data-scm-subform-row={`${formName}_${rowIndex}`}
        >
          {isWrite && rows.length > 1 && (
            <input
              type="checkbox"
              checked={rowData.is_selected === true}
              onChange={(e) => handleSelectRow(rowIndex, e.target.checked)}
              title="Select row"
            />
          )}
          {isWrite && (
            <>
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => handleCopy(rowIndex)}
                title="Copy"
              >
                <Icons iconType="Copy" />
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm text-danger p-0"
                onClick={() => handleDelete(rowIndex)}
                title="Delete"
              >
                <Icons iconType="Delete" size={16} />
              </button>
            </>
          )}
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            {renderDefaultRowChildren(rowData, rowIndex)}
          </div>
        </div>
      ))}
    </div>
  );
};

ScmSubFormField.defaultProps = {
  attributes: {},
  elmtParams: {},
};

export default ScmSubFormField;
